import { useState } from 'react'
import { useMutation, useQuery } from 'urql'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { buildPatientSearchQuery } from 'app/components/contacts/drawer/PmsIntegration'
import type { PatientChoice } from 'app/lib/forms/paperworkRecord'
import { copyToClipboard, copyWhenReady } from 'app/hooks/useCopyToClipboard'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import type { ContactPaperworksQuery } from 'app/generated/gql/graphql'
import { logError } from 'lib/logging'
import {
  CANCEL_PAPERWORK_MUTATION,
  CONTACT_PAPERWORKS_QUERY,
  PAPERWORK_PATIENTS_QUERY,
  RESEND_PAPERWORK_MUTATION,
} from 'app/components/paperwork/graphql'

export type PaperworkRecord = ContactPaperworksQuery['paperworks']['hits'][number]

/** Every packet a contact has been sent. Offices send a handful per patient, not hundreds. */
const ALL_PACKETS = { page: 0, limit: 200 }
const NEWEST_FIRST = { field: `createdDate`, direction: `DESC` } as const

export type UseContactDocumentsReturn = {
  records: PaperworkRecord[]
  fetching: boolean
  failed: boolean
  hasPatientLink: boolean
  patients: PatientChoice[]
  busyId: string | undefined
  copyLink: (link: string) => Promise<void>
  newLink: (record: PaperworkRecord) => Promise<void>
  cancel: (record: PaperworkRecord) => Promise<void>
}

export function useContactDocuments(patientIds: string[]): UseContactDocumentsReturn {
  const [confirm] = useConfirmation()
  // Exact match: patient ids carry hyphens, and a partial match here would show another patient's forms.
  const search = patientIds.length ? `patientId.keyword:(${patientIds.join(` OR `)})` : ``
  const [{ data, fetching, error }] = useQuery({
    query: CONTACT_PAPERWORKS_QUERY,
    variables: { search, pagination: ALL_PACKETS, sort: NEWEST_FIRST },
    pause: !search,
    requestPolicy: `cache-and-network`,
  })
  const [{ data: patientData }] = useQuery({
    query: PAPERWORK_PATIENTS_QUERY,
    variables: { search: buildPatientSearchQuery(patientIds), pagination: { page: 0, limit: patientIds.length } },
    pause: !search,
  })
  const [, executeResend] = useMutation(RESEND_PAPERWORK_MUTATION)
  const [, executeCancel] = useMutation(CANCEL_PAPERWORK_MUTATION)
  const [busyId, setBusyId] = useState<string>()

  const copyLink = async (link: string) => {
    try {
      await copyToClipboard(link, `Link copied`)
    } catch (err) {
      logError(`useContactDocuments: copy failed`, err)
      notifyError(`Couldn't copy the link`)
    }
  }

  const newLink = async (record: PaperworkRecord) => {
    const confirmed = await confirm({
      title: `Make a new link`,
      description: `The current link stops working and the new one lasts 14 days. The patient is not sent a text.`,
      variant: `info`,
      submitLabel: `Make new link`,
    }).then(
      () => true,
      () => false,
    )
    if (!confirmed) return
    setBusyId(record.id)
    const link = executeResend({ id: record.id }).then(({ data, error }) => {
      if (error || !data) throw error ?? new Error(`resendPaperwork returned no data`)
      return data.resendPaperwork.link
    })
    const copied = copyWhenReady(link, `New link copied`).then(
      () => true,
      (err) => {
        logError(`useContactDocuments: copy failed`, err)
        return false
      },
    )
    try {
      await link
    } catch (err) {
      notifyError(`Couldn't make a new link`)
      logError(`useContactDocuments: resend failed`, err)
      return
    } finally {
      setBusyId(undefined)
    }
    if (!(await copied)) notifySuccess(`New link made. Use Copy link to copy it.`)
  }

  const cancel = async (record: PaperworkRecord) => {
    const confirmed = await confirm({
      title: `Cancel paperwork`,
      description: `The link stops working. Forms the patient already finished are kept.`,
      variant: `danger`,
      submitLabel: `Cancel paperwork`,
      cancelLabel: `Keep it`,
    }).then(
      () => true,
      () => false,
    )
    if (!confirmed) return
    setBusyId(record.id)
    const { error: cancelError } = await executeCancel({ id: record.id })
    setBusyId(undefined)
    if (cancelError) {
      notifyError(`Couldn't cancel the paperwork`)
      logError(`useContactDocuments: cancel failed`, cancelError.message)
      return
    }
    notifySuccess(`Paperwork cancelled`)
  }

  return {
    records: data?.paperworks.hits ?? [],
    fetching: fetching && !data,
    failed: !!error,
    hasPatientLink: !!search,
    patients: patientData?.patients.hits ?? [],
    busyId,
    copyLink,
    newLink,
    cancel,
  }
}
